import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { Icon, TI } from "../../icons";
import { Glyph } from "../../components/ui";
import { Button, SectionHeader, List, ListRow, Lead, Tag } from "../../components/ui/ds";
import { iconChipStyle } from "../../lib/icons-resolve";
import { LIFE_CAT } from "../../lib/constants";
import { joinedLabel } from "../../lib/format";
import { ChallengeInfoModal } from "./ChallengeInfoModal";

// v13 C1/C2 — dedicated Výzvy screen: official 3×3 + ongoing public, sortable by popularity.
const DIFF_LABEL = { easy: "Ľahká", medium: "Stredná", hard: "Ťažká" };
const CAT_ORDER = ["sport", "pohoda", "produktivita"];

export function ChallengesTab({ reload, embedded = false, me }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("popular");
  const [onlyJoined, setOnlyJoined] = useState(false);  // §7.1 — filter: zapojené vs všetky
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);  // v24 — cid → detail modal
  const load = useCallback(async () => {
    try { setData(await api("/api/challenges/public?sort=" + sort)); }
    catch { setData({ challenges: [], category_labels: {} }); }
  }, [sort]);
  useEffect(() => { load(); }, [load]);
  async function join(cid) { setBusy(cid); try { await api("/api/challenges/" + cid + "/join", { method: "POST" }); await load(); reload(); } catch (e) { alert(e.message); } setBusy(null); }
  if (!data) return <div style={{ ...st.empty, marginTop: 40 }}><div className="spin" /></div>;
  const labels = data.category_labels || {};
  const all = data.challenges || [];
  const official = all.filter((c) => c.is_official);
  const byCat = {};
  official.forEach((c) => { (byCat[c.category] = byCat[c.category] || []).push(c); });
  const cats = [...CAT_ORDER.filter((k) => byCat[k]), ...Object.keys(byCat).filter((k) => !CAT_ORDER.includes(k))];
  const diffIdx = (d) => ["easy", "medium", "hard"].indexOf(d);
  // v33 — riadok výzvy v ROVNAKOM štýle ako zoznam návykov (List/ListRow/Lead primitívy);
  // patrí dovnútra <List> (spoločný rám + hairline oddeľovače).
  function row(c, showDiff) {
    const lc = c.life_cat ? LIFE_CAT[c.life_cat] : null;
    const meta = (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        <Tag tone="brand" size="xs" bare>+{c.points}</Tag>
        {showDiff && c.difficulty && <Tag tone="neutral" size="xs">{DIFF_LABEL[c.difficulty]}</Tag>}
        {lc && <Tag tone="neutral" size="xs" icon={lc.icon}>{lc.label}</Tag>}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "var(--neutral-text-muted)" }}>
          <Icon name="friends" size={12} color="currentColor" /> {c.participants}
        </span>
      </span>
    );
    const trailing = c.joined
      ? <span style={{ ...st.taskDesc, color: "var(--accent)", display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0 }}><Icon name="check" size={14} color="var(--accent)" stroke={3} /> {joinedLabel({ lower: true })}</span>
      : <Button variant="secondary" size="sm" onClick={(e) => { if (e) e.stopPropagation(); join(c.id); }} disabled={busy === c.id} style={{ flexShrink: 0 }}>{busy === c.id ? "…" : "Zapojiť sa"}</Button>;
    return (
      <ListRow key={c.id} onClick={() => setDetail(c.id)}
        lead={<Lead tone="outline" size="sm" style={{ ...iconChipStyle(c.icon_color), borderColor: "var(--neutral-border-strong)" }}><Glyph icon={c.icon} size={17} fallback="challenge" /></Lead>}
        title={c.name} meta={meta} trailing={trailing} />
    );
  }
  return (
    <>
      {!embedded && <div style={st.heroHead}><h1 style={st.heroTitle}>Zapoj sa.</h1></div>}
      <div style={{ ...st.taskDesc, ...(embedded ? { marginTop: 4 } : {}) }}>Zapoj sa do oficiálnych výziev alebo do prebiehajúcich verejných. Plň ich denne a stúpaj v rebríčku.</div>

      <div style={{ ...st.progressHead, marginTop: 18 }}><span className="bz-subhead"><TI name="badge" color="var(--honey)" />Oficiálne výzvy</span></div>
      {cats.length === 0 && <div style={st.taskDesc}>Zatiaľ žiadne oficiálne výzvy.</div>}
      {cats.map((cat) => (
        <div key={cat} style={{ marginTop: 10 }}>
          <SectionHeader label={labels[cat] || cat} />
          {/* v33c — každá výzva ako samostatná dlaždica (ako návyky v Dnes), nie jeden zdieľaný List */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            {[...byCat[cat]].sort((a, b) => diffIdx(a.difficulty) - diffIdx(b.difficulty)).map((c) => <List key={c.id}>{row(c, true)}</List>)}
          </div>
        </div>
      ))}

      <div style={{ ...st.progressHead, marginTop: 22 }}><span className="bz-subhead"><TI name="league" color="var(--plum)" />Prebiehajúce</span></div>
      <div style={{ ...st.seg, marginTop: 4, marginBottom: 8 }}>
        <button onClick={() => setSort("popular")} style={{ ...st.segBtn, ...(sort === "popular" ? st.segOn : {}) }}>Populárne</button>
        <button onClick={() => setSort("new")} style={{ ...st.segBtn, ...(sort === "new" ? st.segOn : {}) }}>Najnovšie</button>
      </div>
      {/* v17 §7 — count pills (mock 135428): Všetky N (dark-filled active) · ✓ Len zapojené M */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button type="button" onClick={() => setOnlyJoined(false)} style={{ ...st.chalFilterPill, ...(!onlyJoined ? st.chalFilterPillOn : {}) }}>
          Všetky <span style={{ ...st.chalFilterCount, ...(!onlyJoined ? { color: "rgba(255,255,255,0.7)" } : {}) }}>{all.length}</span>
        </button>
        <button type="button" onClick={() => setOnlyJoined(true)} style={{ ...st.chalFilterPill, ...(onlyJoined ? st.chalFilterPillOn : {}) }}>
          <Icon name="check" size={13} color="currentColor" stroke={3} /> Len zapojené <span style={{ ...st.chalFilterCount, ...(onlyJoined ? { color: "rgba(255,255,255,0.7)" } : {}) }}>{all.filter((c) => c.joined).length}</span>
        </button>
      </div>
      {(() => { const list = onlyJoined ? all.filter((c) => c.joined) : all;
        return list.length === 0 ? <div style={st.taskDesc}>{onlyJoined ? "Zatiaľ nie si zapojený/á v žiadnej výzve." : "Zatiaľ žiadne verejné výzvy."}</div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{list.map((c) => <List key={c.id}>{row(c, false)}</List>)}</div>; })()}
      {detail != null && <ChallengeInfoModal cid={detail} me={me} onClose={() => setDetail(null)}
        onJoin={async (id) => { await join(id); setDetail(null); }} joinBusy={busy === detail} />}
    </>
  );
}
